import { OutboundProcess } from '@features/supplyChain/types';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { SortableHeader } from '../../../tailwindComponents/SortableHeader';
import { OutboundSortField, SortDirection } from '../types';
import { CollapsibleOutboundRow } from './CollapsibleOutboundRow';

interface OutboundShipmentsTableProps {
  outboundProcesses: OutboundProcess[];
  sortField: OutboundSortField | null;
  sortDirection: SortDirection;
  onSort: (field: OutboundSortField) => void;
  selectedIds: Set<string>;
  onSelectChange: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onOpenProcess: (outboundProcess: OutboundProcess) => void;
}

export function OutboundShipmentsTable({
  outboundProcesses,
  sortField,
  sortDirection,
  onSort,
  selectedIds,
  onSelectChange,
  onSelectAll,
  onOpenProcess,
}: OutboundShipmentsTableProps) {
  const allSelected =
    outboundProcesses.length > 0 &&
    outboundProcesses.every((p) => selectedIds.has(p.id));

  return (
    <div>
      {/* Header */}
      <div className='flex items-center gap-5 bg-gray-100 p-5'>
        <ChevronRightIcon className='h-6 w-6 shrink-0 text-gray-400' />
        <SortableHeader<OutboundSortField>
          label='Status'
          field='noteId'
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
          className='max-w-[160px] min-w-[120px] justify-center'
        />
        <SortableHeader<OutboundSortField>
          label='Note ID'
          field='noteId'
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <SortableHeader<OutboundSortField>
          label='From'
          field='created'
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <SortableHeader<OutboundSortField>
          label='To'
          field='to'
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
        />
        <SortableHeader<OutboundSortField>
          label='SKUs'
          field='skus'
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
          className='max-w-[150px] min-w-[74px]'
        />
        <SortableHeader<OutboundSortField>
          label='Pallets'
          field='pallets'
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
          className='max-w-[150px] min-w-[74px]'
        />
        {/* Checkbox select all */}
        <div className='shrink-0'>
          <input
            type='checkbox'
            checked={allSelected}
            onChange={(e) => onSelectAll(e.target.checked)}
            className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
          />
        </div>
      </div>

      {/* Rows */}
      <div className='flex flex-col'>
        {outboundProcesses.length === 0 ? (
          <div className='border-t border-gray-200 px-5 py-8 text-center text-sm text-gray-500'>
            No outbound shipments found
          </div>
        ) : (
          outboundProcesses.map((outboundProcess) => (
            <CollapsibleOutboundRow
              key={outboundProcess.id}
              outboundProcess={outboundProcess}
              selected={selectedIds.has(outboundProcess.id)}
              onSelectChange={onSelectChange}
              onOpenProcess={onOpenProcess}
            />
          ))
        )}
      </div>
    </div>
  );
}
